<template>
  <f7-page name="invitation-accept" class="bg-surface font-body-md text-on-surface">
    <f7-navbar :title="$t('groupInvite.title')" back-link no-shadow />
    <AppLayout>
      <template #main>
        <f7-block v-if="loading" class="text-align-center">
          <f7-preloader />
        </f7-block>

        <f7-block v-else-if="error" strong inset>
          <div class="bg-error-container rounded-xl p-4 text-on-error-container">
            <div class="display-flex align-items-center gap-2 mb-1">
              <f7-icon material="error" :size="20" />
              <h3 class="font-label-lg font-semibold">{{ $t("groupInvite.errorTitle") }}</h3>
            </div>
            <p class="font-body-sm text-on-surface-variant">{{ error }}</p>
          </div>
        </f7-block>

        <template v-else-if="preview">
          <div class="px-4 pt-4">
            <!-- Top badge -->
            <div class="display-flex justify-content-center mb-3">
              <div class="display-inline-flex align-items-center gap-1.5 px-4 py-1 bg-secondary-container/30 text-on-secondary-fixed rounded-full shadow-avocash-sm">
                <f7-icon material="workspace_premium" :size="16" class="text-secondary" />
                <span class="font-label-sm uppercase tracking-wider font-semibold">{{ $t("groupInvite.exclusive") }}</span>
              </div>
            </div>

            <!-- Main Invitation Hero Card -->
            <f7-card
              class="accept-hero bg-surface-pure rounded-xl shadow-avocash-md relative overflow-hidden"
              :padding="false"
            >
              <div class="absolute -top-12 -right-12 w-36 h-36 bg-primary-fixed/30 rounded-full blur-2xl pointer-events-none"></div>
              <div class="absolute -bottom-8 -left-8 w-28 h-28 bg-secondary-container/20 rounded-full blur-xl pointer-events-none"></div>

              <f7-card-content class="accept-hero-content" :padding="false">
                <div class="display-flex flex-direction-column align-items-center text-align-center relative z-10">
                  <div class="relative mb-3">
                    <div class="w-20 h-20 rounded-full overflow-hidden p-1 bg-surface-container-low shadow-avocash-sm display-flex align-items-center justify-content-center">
                      <img
                        v-if="preview.groupLogo"
                        class="width-100 h-full object-cover rounded-full"
                        :src="preview.groupLogo"
                        :alt="preview.groupName"
                      />
                      <div v-else class="width-100 h-full rounded-full bg-primary-fixed display-flex align-items-center justify-content-center">
                        <f7-icon material="groups" :size="32" class="text-primary" />
                      </div>
                    </div>
                    <div v-if="preview.verified" class="absolute -bottom-2 -right-2 bg-primary text-on-primary rounded-full px-2 py-0.5 display-flex align-items-center gap-0.5 shadow-avocash-sm">
                      <f7-icon material="verified" :size="13" />
                      <span class="font-label-sm leading-tight font-medium">{{ $t("groupInvite.verified") }}</span>
                    </div>
                  </div>
                  <h2 class="font-headline-md text-on-surface mb-1">{{ preview.groupName }}</h2>
                  <p class="font-body-sm text-on-surface-variant max-w-xs">
                    <span class="font-semibold text-primary">{{ preview.senderName || preview.firstName || $t("groupInvite.friend") }}</span>
                    {{ $t("groupInvite.invitesYou") }}
                    <span class="font-bold text-on-surface">avocash</span>.
                  </p>

                  <!-- Quote note -->
                  <div v-if="preview.message" class="width-100 bg-surface-container-low rounded-lg p-3 text-align-left mb-2">
                    <div class="display-flex align-items-flex-start gap-2">
                      <f7-icon material="format_quote" :size="20" class="text-primary flex-shrink-0 mt-0.5" />
                      <p class="font-body-sm text-on-surface italic leading-relaxed">« {{ preview.message }} »</p>
                    </div>
                  </div>
                </div>
              </f7-card-content>
            </f7-card>

            <!-- Trust metrics bento -->
            <div class="mt-3 grid grid-cols-2 gap-2">
              <f7-card class="trust-tile bg-surface-pure rounded-lg shadow-avocash-sm" :padding="false">
                <f7-card-content class="trust-tile-content" :padding="false">
                  <div class="display-flex align-items-center justify-content-space-between mb-3">
                    <div class="w-8 h-8 rounded-full bg-primary-fixed/40 display-flex align-items-center justify-content-center text-primary">
                      <f7-icon material="group" :size="18" />
                    </div>
                    <span class="font-label-sm text-primary font-semibold">{{ $t("groupInvite.active") }}</span>
                  </div>
                  <div>
                    <span class="font-headline-sm text-on-surface display-block">{{ preview.membersCount ?? "—" }}</span>
                    <span class="font-body-sm text-on-surface-variant">{{ $t("groupInvite.membersGathered") }}</span>
                  </div>
                </f7-card-content>
              </f7-card>
              <f7-card class="trust-tile bg-surface-pure rounded-lg shadow-avocash-sm" :padding="false">
                <f7-card-content class="trust-tile-content" :padding="false">
                  <div class="display-flex align-items-center justify-content-space-between mb-3">
                    <div class="w-8 h-8 rounded-full bg-secondary-container display-flex align-items-center justify-content-center text-on-secondary-container">
                      <span class="material-symbols-outlined" style="font-size: 18px">shield_with_heart</span>
                    </div>
                    <span class="font-label-sm font-semibold text-secondary">{{ $t("groupInvite.goldTier") }}</span>
                  </div>
                  <div>
                    <span class="font-headline-sm text-on-surface display-block">{{ preview.trustScore ?? "—" }}%</span>
                    <span class="font-body-sm text-on-surface-variant">{{ $t("groupInvite.trustIndex") }}</span>
                  </div>
                </f7-card-content>
              </f7-card>
            </div>

            <!-- Perks -->
            <f7-card class="perks-card mt-3 bg-surface-pure rounded-xl shadow-avocash-sm" :padding="false">
              <f7-card-content class="perks-card-content" :padding="false">
                <h3 class="font-label-lg text-on-surface font-semibold mb-3 display-flex align-items-center gap-2">
                  <f7-icon material="verified_user" :size="18" class="text-primary" />
                  {{ $t("groupInvite.memberPerks") }}
                </h3>
                <div class="display-flex flex-direction-column gap-3">
                  <div class="display-flex align-items-flex-start gap-2.5">
                    <div class="w-8 h-8 rounded-full bg-primary-fixed/40 text-primary display-flex align-items-center justify-content-center flex-shrink-0">
                      <f7-icon material="payments" :size="18" />
                    </div>
                    <div class="min-w-0">
                      <div class="font-label-md text-on-surface">{{ $t("groupInvite.perkFees") }}</div>
                      <div class="font-body-sm text-on-surface-variant">{{ $t("groupInvite.perkFeesDesc") }}</div>
                    </div>
                  </div>
                  <div class="display-flex align-items-flex-start gap-2.5">
                    <div class="w-8 h-8 rounded-full bg-primary-fixed/40 text-primary display-flex align-items-center justify-content-center flex-shrink-0">
                      <f7-icon material="history_edu" :size="18" />
                    </div>
                    <div class="min-w-0">
                      <div class="font-label-md text-on-surface">{{ $t("groupInvite.perkSignatures") }}</div>
                      <div class="font-body-sm text-on-surface-variant">{{ $t("groupInvite.perkSignaturesDesc") }}</div>
                    </div>
                  </div>
                </div>
              </f7-card-content>
            </f7-card>

            <!-- Acceptance form card -->
            <f7-card class="identity-card mt-3 bg-surface-container-low rounded-xl shadow-avocash-sm" :padding="false">
              <f7-card-content class="identity-card-content" :padding="false">
                <div class="display-flex align-items-center justify-content-space-between mb-3">
                  <span class="font-label-md text-on-surface uppercase tracking-wider font-semibold">{{ $t("groupInvite.guestIdentity") }}</span>
                  <div v-if="preview.expiresAt" class="display-inline-flex align-items-center gap-1 text-tertiary font-label-sm font-semibold">
                    <f7-icon material="timer" :size="14" />
                    {{ $t("groupInvite.expiresOn", { date: $dayjs(preview.expiresAt).format("ll") }) }}
                  </div>
                </div>

                <div class="bg-surface-pure rounded-lg p-2.5 mb-3 display-flex align-items-center justify-content-space-between">
                  <div class="display-flex align-items-center gap-2.5">
                    <div class="w-10 h-10 rounded-full bg-primary-fixed text-primary display-flex align-items-center justify-content-center font-headline-sm">{{ initial }}</div>
                    <div class="min-w-0">
                      <div class="font-label-lg text-on-surface display-flex align-items-center gap-1 truncate">
                        {{ preview.email || preview.phoneNumber || $t("groupInvite.friend") }}
                        <f7-icon material="check_circle" :size="14" class="text-primary" />
                      </div>
                      <div class="font-body-sm text-on-surface-variant truncate">{{ preview.sentTo || preview.email || preview.phoneNumber }}</div>
                    </div>
                  </div>
                </div>

                <div class="display-flex flex-direction-column gap-2">
                  <f7-button fill large :disabled="joining" @click="joinNow">
                    <template v-if="joining">{{ $t("groupInvite.joining") }}</template>
                    <template v-else>{{ $t(preview.existingAccount ? "groupInvite.signInAndJoin" : "groupInvite.createAndJoin") }}</template>
                  </f7-button>
                </div>

                <!-- Trust badges -->
                <div class="mt-3 display-flex align-items-center justify-content-center gap-4 text-on-surface-variant opacity-80">
                  <div class="display-flex align-items-center gap-1 font-label-sm">
                    <f7-icon material="lock" :size="14" />
                    {{ $t("groupInvite.encryption") }}
                  </div>
                  <div class="display-flex align-items-center gap-1 font-label-sm">
                    <f7-icon material="verified" :size="14" />
                    {{ $t("groupInvite.regulated") }}
                  </div>
                </div>
              </f7-card-content>
            </f7-card>
          </div>
        </template>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../components/common/AppLayout.vue";
import { useMembersStore } from "../js/pinia-stores/members.js";
import { useKeycloakStore } from "../js/pinia-stores/keycloak.js";
import base_url from "../js/store/base_url.json";
import i18n from "../js/i18n.js";
const props = defineProps({
  token: { type: String, required: true },
});

const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();

const loading = ref(true);
const joining = ref(false);
const preview = ref(null);
const error = ref("");
const pendingInviteKey = "avocash-pending-invite";

onMounted(async () => {
  try {
    if (!keycloakStore.initialized) {
      await keycloakStore.initialize(base_url.keycloakJson);
    }
    preview.value = await membersStore.previewInvitation(props.token);

    const pendingToken = sessionStorage.getItem(pendingInviteKey);
    if (keycloakStore.authenticated && pendingToken === props.token) {
      sessionStorage.removeItem(pendingInviteKey);
      await completeAndRedirect();
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      i18n.global.t("groupInvite.errorTitle") ||
      "Invitation not found";
  } finally {
    loading.value = false;
  }
});

async function joinNow() {
  joining.value = true;
  try {
    await membersStore.startInvitation(props.token);

    if (keycloakStore.authenticated) {
      await completeAndRedirect();
    } else {
      sessionStorage.setItem(pendingInviteKey, props.token);
      await keycloakStore.login();
    }
  } catch (e) {
    error.value =
      e?.response?.data?.message ||
      e?.message ||
      i18n.global.t("groupInvite.errorTitle") ||
      "Unable to join this invitation";
  } finally {
    joining.value = false;
  }
}

async function completeAndRedirect() {
  const result = await membersStore.completeInvitation(props.token);
  f7.toast.show({
    text: i18n.global.t("group.welcomeToTheGroup"),
    closeTimeout: 2000,
  });
  f7.router.navigate(`/groups/${result.groupId}`, {
    clearPreviousHistory: true,
  });
}
</script>

<style scoped>
.text-align-center {
  text-align: center;
  padding: 40px 0;
}

.accept-hero,
.trust-tile,
.perks-card,
.identity-card {
  margin: 0;
}

.accept-hero-content {
  padding: 20px;
}

.trust-tile-content {
  padding: 12px;
}

.perks-card-content,
.identity-card-content {
  padding: 16px;
}
</style>
